import React, { useState } from "react";
import { portfolio } from "@/data/portfolio";
import useReveal from "@/hooks/useReveal";

const Timeline = () => {
  const ref1 = useReveal();
  const { timeline, timelineFootnotes } = portfolio;
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <section id="timeline" data-testid="timeline-section" className="px-5 md:px-10 py-14 md:py-28 relative">
      <div ref={ref1} className="reveal-up mx-auto max-w-[1400px]">
        <div className="flex items-center justify-between mb-6 md:mb-10">
          <div className="section-tag">→ Through the years</div>
          <div className="section-tag">{timeline.length} entries</div>
        </div>

        <ol className="space-y-0" onMouseMove={handleMouseMove}>
          {timeline.map((t, i) => (
            <li
              key={i}
              data-testid={`timeline-item-${i}`}
              className="grid grid-cols-12 gap-2 sm:gap-4 py-4 sm:py-5 hairline-b relative group"
              style={{ borderColor: "var(--rule)" }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="col-span-2 md:col-span-1 font-mono text-xs sm:text-sm tabular transition-colors duration-300 group-hover:text-[var(--accent)]" style={{ color: "var(--ink-muted)" }}>
                {String(i + 1).padStart(2, "0")}.
              </div>
              <div className="col-span-3 md:col-span-2 font-serif text-xl sm:text-2xl md:text-3xl tabular tracking-tightest leading-tight transition-colors duration-300 group-hover:text-[var(--accent)]">
                {t.year}
              </div>
              <div className="col-span-7 md:col-span-6 font-serif text-base sm:text-lg md:text-xl leading-snug transition-transform duration-300 group-hover:translate-x-2">
                {t.label}
              </div>
              <div className="col-span-12 md:col-span-3 md:text-right text-xs sm:text-sm md:pt-2 mt-1 md:mt-0" style={{ color: "var(--ink-muted)" }}>
                {t.place}
              </div>
            </li>
          ))}
        </ol>

        {/* Floating preview image following cursor */}
        {hoveredIndex !== null && (
          <div
            className="pointer-events-none fixed z-[200] hidden lg:block overflow-hidden rounded border"
            style={{
              left: mousePos.x + 24,
              top: mousePos.y - 120,
              width: "280px",
              height: "180px",
              borderColor: "var(--rule)",
              boxShadow: "0 12px 36px rgba(0, 0, 0, 0.18)",
              transition: "left 0.15s ease-out, top 0.15s ease-out",
              animation: "timeline-img-pop 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards",
            }}
          >
            <img
              src={timeline[hoveredIndex].image}
              alt={timeline[hoveredIndex].label}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="flex flex-wrap gap-6 mt-6">
          {timelineFootnotes.map((f, i) => (
            <div key={i} className="font-mono text-[0.72rem] uppercase tracking-microcap" style={{ color: "var(--ink-muted)" }}>
              <sup>{i + 1}</sup> {f}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes timeline-img-pop {
          from {
            opacity: 0;
            transform: scale(0.85) translateY(12px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default Timeline;
